import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxTableFooterDirective } from './directives/table-footer';
import { HxTableRowDirective } from './directives/table-row';
import { HxExpandableRowComponent } from './expandable-row/expandable-row';

/**
 * Harmony Table Component (hx-table)
 *
 * A highly flexible, accessible table component using content projection for maximum flexibility. Allows you to define
 * table structure using directives similar to Angular Material's table.
 *
 * Supports Moon Design System specifications with:
 *
 * - Content projection for headers, rows, and cells
 * - Sortable columns with visual indicators
 * - Custom styling per row/cell
 * - Horizontal and vertical scrolling
 * - Full keyboard navigation (WCAG 2.1 AA compliant)
 * - ARIA support with screen reader announcements
 *
 * @example
 *   ```html
 *   <hx-table>
 *     <hx-table-header>
 *       <hx-header-cell [sortable]="true" [width]="200">Name</hx-header-cell>
 *       <hx-header-cell [sortable]="true" [width]="'25%'">Status</hx-header-cell>
 *       <hx-header-cell [width]="'2fr'">Description</hx-header-cell>
 *     </hx-table-header>
 *
 *     <hx-table-row *ngFor="let user of users">
 *       <hx-table-cell>{{user.name}}</hx-table-cell>
 *       <hx-table-cell>{{user.status}}</hx-table-cell>
 *       <hx-table-cell>{{user.description}}</hx-table-cell>
 *     </hx-table-row>
 *   </hx-table>
 *   ```;
 *
 * @example
 *   ```html
 *   <!-- With custom components -->
 *   <hx-table>
 *     <hx-table-header>
 *       <hx-header-cell>Status</hx-header-cell>
 *     </hx-table-header>
 *
 *     <hx-table-row *ngFor="let item of items">
 *       <hx-table-cell>
 *         <hx-badge [text]="item.status" [variant]="item.statusVariant"></hx-badge>
 *       </hx-table-cell>
 *     </hx-table-row>
 *   </hx-table>
 *   ```;
 */
@Component({
  selector: 'hx-table',
  standalone: true,
  imports: [],
  templateUrl: './table.html',
  styleUrl: './table.css',
  exportAs: 'hxTable',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class HxTableComponent extends HxBaseControl {
  /** ARIA label for the table */
  override readonly ariaLabel = input<string>('Data table');

  /** Maximum height for scrolling */
  readonly maxHeight = input<string | null>('600px');

  /**
   * When true the header row stays pinned while the rows scroll underneath it, bounded by `freezeHeaderMaxHeight`.
   * Tables shorter than that cap still render in full — no empty space is added below the last row.
   */
  readonly freezeHeader = input<boolean>(false);

  /** Cap applied to the scrollable area while `freezeHeader` is enabled. */
  readonly freezeHeaderMaxHeight = input<string>('min(60vh, 36rem)');

  /**
   * Whether the footer is fixed/sticky at the bottom. When true, the table will use maxHeight as its height and the
   * footer will remain fixed at the bottom.
   */
  readonly footerFixed = input<boolean>(false);

  /**
   * Row height size for table rows.
   *
   * - 'xs': 28px
   * - 'sm': 32px
   * - 'md': 40px (default)
   * - 'lg': 48px
   */
  readonly rowHeight = input<'xs' | 'sm' | 'md' | 'lg'>('md');

  /**
   * Whether the table and all its child components are disabled. When true, all interactive components inside the table
   * will be disabled.
   */
  readonly disabled = input<boolean>(false);

  /** Enable row hover effect */
  readonly enableRowHover = input<boolean>(true);

  /** Custom hover background color (defaults to token value) */
  readonly hoverColor = input<string | undefined>('#e8e8e8');

  /**
   * Hover effect variant for table rows.
   *
   * - 'none': No hover effect
   * - 'color': Background color change on hover (default)
   * - 'shadow': Shadow effect on hover
   * - 'focus': Focus ring style on hover
   */
  readonly hoverVariant = input<'none' | 'color' | 'shadow' | 'focus'>('color');

  /** Message to display when no data is available */
  readonly noDataMessage = input<string>('No data available');

  /** Enable column resizing by dragging */
  readonly resizableColumns = input<boolean>(true);

  /**
   * Visual variant for the table header.
   *
   * - 'default': Borders with vertical divider lines between cells
   * - 'outline': Border around header, no vertical divider lines between cells
   * - 'dividers': No border around header, only vertical divider lines between cells
   * - 'borderless': No borders, no dividers
   */
  readonly headerVariant = input<'default' | 'outline' | 'dividers' | 'borderless'>('default');

  /**
   * Visual variant for the table rows.
   *
   * - 'default': Borders with vertical divider lines between cells
   * - 'outline': Border around rows, no vertical divider lines between cells
   * - 'dividers': No border around rows, only vertical divider lines between cells
   * - 'borderless': No borders, no dividers
   */
  readonly rowVariant = input<'default' | 'outline' | 'dividers' | 'borderless'>('default');

  /**
   * @deprecated This property is no longer needed. The table always uses grid mode with subgrid for alignment. Kept for
   *   backward compatibility - value is ignored.
   */
  readonly equalColumns = input<boolean>(true);

  /** Grid template columns from headers */
  readonly gridTemplateColumns = signal('auto');

  /** View children for scroll synchronization */
  readonly headerElement = viewChild<ElementRef<HTMLDivElement>>('headerContainer');
  readonly bodyElement = viewChild<ElementRef<HTMLDivElement>>('bodyContainer');
  readonly scrollContainer = viewChild<ElementRef<HTMLDivElement>>('scrollContainer');

  /** Content children - table rows (including nested rows inside sections) */
  readonly rows = contentChildren(HxTableRowDirective, { descendants: true });

  /** Content children - expandable rows */
  readonly expandableRows = contentChildren(HxExpandableRowComponent, { descendants: true });

  /** Content children - footer */
  readonly footers = contentChildren(HxTableFooterDirective, { descendants: true });

  /** Computed signal to track if table has content */
  readonly hasContent = computed(() => this.rows().length > 0 || this.expandableRows().length > 0);

  /** Computed signal to track if footer has content */
  readonly hasFooter = computed(() => this.footers().length > 0);

  /**
   * Effective vertical cap for the scrollable area. `null` means the table grows with its content and never scrolls
   * vertically. Only `freezeHeader` or an explicit `maxHeight` (other than `auto`/`none`) introduces a scrollbar.
   */
  readonly scrollMaxHeight = computed<string | null>(() => {
    if (this.freezeHeader()) {
      return this.freezeHeaderMaxHeight();
    }

    const max = this.maxHeight()?.trim();

    return !max || max === 'auto' || max === 'none' ? null : max;
  });

  /** Emits the final grid column widths once a drag-resize finishes. */
  readonly columnsResized = output<string[]>();

  constructor() {
    super();
  }

  updateGridColumns(columns: string): void {
    this.gridTemplateColumns.set(columns);
  }

  /** Called by header cells when a drag-resize completes. */
  notifyColumnResizeEnd(widths: string[]): void {
    this.columnsResized.emit(widths);
  }
}
